import {
  ApiChartAttribute,
  ApiChartLayout,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box, Stack, Typography } from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import Loading from "Components/Loading/Loading";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_BESTSELLERS_TABLE,
  CHART_KEY_DASHBOARD_BESTSELLERS_TREND,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";
import ProductAnalyticsCard from "features/products/components/ProductAnalyticsCard";
import { useTopPerformingProducts } from "features/products/hooks/useTopPerformingProducts";
import { ProductTrendProvider } from "features/products/providers/ProductTrendProvider";
import { useSelectedProject } from "Hooks";
import { useIsMobile } from "Hooks/useIsMobile";
import { useProducts } from "Hooks/useProducts";
import { RouteNames } from "Hooks/routeNames";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

interface IBestsellersTabProps {
  attributes: ApiChartAttribute[];
}

function BestsellersTabInner({ attributes }: IBestsellersTabProps) {
  const isMobile = useIsMobile();
  const history = useHistory();
  const { selectedProject, selectedClient } = useSelectedProject();
  const { pathByName } = useProtectedRoutes();
  const { products } = useProducts();
  const {
    products: topProducts,
    loading,
    tabConfig,
  } = useTopPerformingProducts(attributes, "top_sellers");
  const topProduct = topProducts[0];

  const onProductClick = useCallback(
    (productId?: number) => {
      if (!productId || !selectedProject?.id || !selectedClient?.id) {
        return;
      }

      history.push(
        pathByName(RouteNames.PRODUCT_DEEP_DIVE, {
          clientId: selectedClient.id,
          projectId: selectedProject.id,
        }),
        { selectedProductId: productId },
      );
    },
    [history, pathByName, selectedClient?.id, selectedProject?.id],
  );

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%" }}
      >
        <Box
          sx={{
            height: 400,
            width: { xs: "100%", md: "33.333%" },
            flexShrink: 0,
          }}
        >
          <GlassCard
            sx={{ height: "100%" }}
            boxProps={{
              p: 2.5,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} mb={2}>
              Top Performer
            </Typography>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "stretch",
              }}
            >
              {loading && !topProduct ? (
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <Loading />
                </Box>
              ) : !topProduct ? (
                <Typography
                  color="text.secondary"
                  textAlign="center"
                  sx={{ m: "auto" }}
                >
                  No product performance data for this period.
                </Typography>
              ) : (
                <ProductAnalyticsCard
                  product={topProduct}
                  products={products}
                  trendMetric={tabConfig.trendMetric}
                  leadMetric={tabConfig.leadMetric}
                  fullWidth
                  onClick={onProductClick}
                />
              )}
            </Box>
          </GlassCard>
        </Box>

        <Box sx={{ height: 400, flex: 1, minWidth: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_BESTSELLERS_TREND}
            staticAttributes={attributes}
            ignoreAttrModel
            chart={{
              name: "Top Products by Revenue",
              dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 8,
              orderBy: `${ApiMetric.METRIC_PRODUCT_REVENUE} DESC`,
              having: [
                {
                  metric: ApiMetric.METRIC_PRODUCT_REVENUE,
                  operator: ApiOperator.IS_GREATER_THAN,
                  value: 0,
                },
              ],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_PRODUCT_REVENUE],
                  chart: {
                    [ApiMetric.METRIC_PRODUCT_REVENUE]: ApiYAxisChartType.BAR,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                layout: ApiChartLayout.VERTICAL,
                noLegend: true,
              },
            }}
          />
        </Box>
      </Stack>

      <Box sx={{ height: table5Rows, flexShrink: 0, width: "100%" }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_BESTSELLERS_TABLE}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Top Products",
            dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiMetric.METRIC_PRODUCT_REVENUE} DESC`,
            having: [
              {
                metric: ApiMetric.METRIC_PRODUCT_REVENUE,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SALE_ITEMS_QUANTITY,
                  ApiMetric.METRIC_PRODUCT_REVENUE,
                  ApiMetric.METRIC_SALES_COUNT,
                ],
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              noFilters: true,
              rowsPerPage: 0,
              showTotals: true,
              tableColWidths: {
                [ApiDimension.DIMENSION_PRODUCT_NAME]: isMobile ? 200 : 400,
              },
            },
          }}
        />
      </Box>
    </>
  );
}

function BestsellersTab({ attributes }: IBestsellersTabProps) {
  return (
    <ProductTrendProvider chartAttributes={attributes}>
      <BestsellersTabInner attributes={attributes} />
    </ProductTrendProvider>
  );
}

export default BestsellersTab;
